/**
 * Vue 3 Composables - Replacement for getCurrentInstance().proxy patterns
 *
 * These composables provide clean access to Vue 3 APIs that replace
 * the deprecated getCurrentInstance().proxy usage in Vue 2/3 migration.
 */

import { inject, nextTick as vueNextTick, ref, getCurrentInstance, h } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { confirm as customConfirm, alert as customAlert } from "../services/confirmDialog.service";

/**
 * Main composable that provides access to common Vue instance properties
 * Replaces: const vm = getCurrentInstance().proxy
 *
 * @returns {Object} Object containing router, route, store, and injected services
 */
export function useVueInstance() {
	const router = useRouter();
	const route = useRoute();
	const store = useStore();

	// Access injected services (configured in main.js)
	const $toast = useToast(); // Use the composable directly
	const $dialog = inject("$dialog");

	return {
		router, // Replaces vm.$router
		route, // Replaces vm.$route
		store, // Replaces vm.$store
		$toast, // Replaces vm.$toast (injected service)
		$dialog, // Replaces vm.$dialog (injected service)
	};
}

/**
 * Router composable for navigation
 * Replaces: vm.$router.push() patterns
 *
 * @returns {Object} Router instance and helper methods
 */
export function useVueRouter() {
	const router = useRouter();

	const navigateTo = (to) => router.push(to);
	const navigateBack = () => router.back();
	const navigateReplace = (to) => router.replace(to);

	return {
		router,
		navigateTo,
		navigateBack,
		navigateReplace,
	};
}

/**
 * Route composable for accessing route information
 * Replaces: vm.$route patterns
 *
 * @returns {Object} Route instance and helper properties
 */
export function useVueRoute() {
	const route = useRoute();

	return {
		route,
		routeName: route.name,
		routePath: route.path,
		routeParams: route.params,
		routeQuery: route.query,
		routeMeta: route.meta,
	};
}

/**
 * Store composable for Vuex state management
 * Replaces: vm.$store patterns
 *
 * @returns {Object} Store instance and helper methods
 */
export function useVueStore() {
	const store = useStore();

	const dispatch = (action, payload) => store.dispatch(action, payload);
	const commit = (mutation, payload) => store.commit(mutation, payload);
	const getters = store.getters;
	const state = store.state;

	return {
		store,
		dispatch,
		commit,
		getters,
		state,
	};
}

/**
 * Notification service composable
 * Replaces: vm.$toast patterns
 *
 * @returns {Object} Toast service instance
 */
export function useNotifications() {
	const $toast = useToast(); // Use direct composable
	const hasMessage = (message) => {
		if (typeof message === "string") return message.trim().length > 0;
		return message !== null && typeof message !== "undefined";
	};

	const createTitleContent = (title, message) => {
		return h("div", [
			h("div", { style: { fontWeight: "bold", marginBottom: "4px", color: "white" } }, title),
			h("div", { style: { color: "white" } }, message),
		]);
	};

	return {
		$toast,
		success: (message, title) => {
			if (!hasMessage(message)) return;
			return title ? $toast.success(createTitleContent(title, message)) : $toast.success(message);
		},
		error: (message, title) => {
			if (!hasMessage(message)) return;
			return title ? $toast.error(createTitleContent(title, message)) : $toast.error(message);
		},
		info: (message, title) => {
			if (!hasMessage(message)) return;
			return title ? $toast.info(createTitleContent(title, message)) : $toast.info(message);
		},
		warning: (message, title) => {
			if (!hasMessage(message)) return;
			return title ? $toast.warning(createTitleContent(title, message)) : $toast.warning(message);
		},
	};
}

/**
 * Dialog service composable
 * Replaces: vm.$dialog patterns
 *
 * @returns {Object} Dialog service instance
 */
export function useDialogs() {
	return {
		confirm: (message, options = {}) => {
			return customConfirm(message, options);
		},
		alert: (message, options = {}) => {
			return customAlert(message, options);
		},
	};
}

/**
 * NextTick composable
 * Replaces: vm.$nextTick patterns
 *
 * @returns {Function} nextTick function
 */
export function useNextTick() {
	return vueNextTick;
}

/**
 * Template refs composable helper
 * Provides utilities for working with template refs in composition API
 * Replaces: vm.$refs patterns
 *
 * Usage:
 * const { createTemplateRef } = useTemplateRefs()
 * const myRef = createTemplateRef()
 * // In template: <div ref="myRef">
 *
 * @returns {Object} Template ref utilities
 */
export function useTemplateRefs() {
	const createTemplateRef = (initialValue = null) => {
		return ref(initialValue);
	};

	return {
		createTemplateRef,
	};
}

/**
 * Legacy compatibility helper
 * For cases where you need to gradually migrate from getCurrentInstance patterns
 *
 * @deprecated Use specific composables instead
 * @returns {Object} Object containing all common instance properties
 */
export function useLegacyVueInstance() {
	console.warn("useLegacyVueInstance is deprecated. Use specific composables instead.");

	const router = useRouter();
	const route = useRoute();
	const store = useStore();
	const $toast = useToast(); // Use direct composable
	const $dialog = inject("$dialog");

	return {
		$router: router,
		$route: route,
		$store: store,
		$toast,
		$dialog,
		$nextTick: vueNextTick,
	};
}
